<template>
  <div class="list-order-can-not-serve">
    <KTCodePreview v-bind:title="'Danh sách phiếu không phục vụ được'">
      <!-- Toolbar Section -->
      <template v-slot:toolbar>
        <b-row>
          <b-dropdown size="sm" right>
            <template slot="button-content">
              <i style="font-size: 1rem" class="fas fa-cog"></i>
              <span class="font-weight-bolder">Thao tác</span>
            </template>
            <b-dropdown-item @click="exportExcel">
              <span>
                <i style="font-size: 1rem" class="far fa-file-excel"></i>
                &nbsp; Xuất Excel</span
              >
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            class="font-weight-bolder ml-3"
            @click="createNew"
            size="sm"
            variant="primary"
          >
            <i style="font-size: 1rem" class="flaticon2-add-1"></i>Tạo mới
          </b-button>
        </b-row>
      </template>

      <!-- Main Content Section -->
      <template v-slot:preview>
        <!-- Filter Section -->
        <b-row class="mb-5">
          <b-col
            md="3"
            class="d-flex align-items-center"
            style="column-gap: 10px"
          >
            <date-picker
              placeholder="Từ ngày"
              class="form-control form-control-sm w-100"
              v-model="filters.fromDate"
              :config="configs.date"
            >
            </date-picker>
            <date-picker
              placeholder="Đến ngày"
              class="form-control form-control-sm w-100"
              v-model="filters.toDate"
              :config="configs.date"
            >
            </date-picker>
          </b-col>

          <b-col>
            <b-input
              size="sm"
              placeholder="Tìm kiếm tên KH, SĐT"
              v-model.trim="filters.customerPhoneName"
              append-icon="search"
              single-line
              hide-details
              v-on:keyup.enter="onFilter()"
            ></b-input>
          </b-col>

          <b-col>
            <AutoSuggest
              :model="employeeSearchText"
              :suggestions="employeeSuggestions"
              :placeholder="'tên nhân viên'"
              :suggestion-name="'label'"
              @change="searchEmployees"
              @select="selectEmployee"
            >
              <div slot="custom" slot-scope="{ suggestion }">
                {{ suggestion.item.label }} - {{ suggestion.item.code }}
              </div>
            </AutoSuggest>
          </b-col>

          <b-col>
            <BDropdownCustom
              v-model="filters.stores"
              :options="storeOptions"
              :multiple="true"
              :searchable="true"
              value-prop="value"
              text-prop="text"
              placeholder="chi nhánh"
            />
          </b-col>

          <b-col>
            <BDropdownCustom
              v-model="filters.reasons"
              :options="reasonOptions"
              :multiple="true"
              :searchable="false"
              value-prop="value"
              text-prop="text"
              placeholder="lý do"
            />
          </b-col>
        </b-row>

        <b-row class="mb-5">
          <b-col md="3">
            <b-input
              size="sm"
              placeholder="Tên sản phẩm"
              v-model.trim="filters.productName"
              v-on:keyup.enter="onFilter()"
            ></b-input>
          </b-col>
          <b-col md="9" class="d-flex justify-content-end">
            <b-button
              style="font-weight: 600; width: 70px"
              variant="primary"
              size="sm"
              @click="onFilter"
              >Lọc</b-button
            >
            <b-button
              style="font-weight: 600; width: 70px"
              size="sm"
              class="ml-2"
              v-b-tooltip.hover
              title="Reset dữ liệu"
              @click="resetFilter"
              variant="secondary"
            >
              <i style="padding-right: 0" class="fa fa-redo"></i>
            </b-button>
          </b-col>
        </b-row>

        <!-- Data Table -->
        <b-table
          :items="orders"
          :fields="fields"
          class="table-bordered table-hover col-md-12"
          no-local-sorting
          :small="true"
          :busy="loading"
        >
          <template v-slot:table-busy>
            <vcl-table :speed="5" :animate="true" :columns="9"></vcl-table>
          </template>

          <!-- STT Cell -->
          <template v-slot:cell(stt)="row">
            <div class="text-center">
              {{ row.item.stt }}
            </div>
          </template>

          <!-- Customer Info Cell -->
          <template v-slot:cell(customerInfo)="row">
            <div class="customer-info">
              <div class="customer-name">{{ row.item.customerName }}</div>
              <div class="customer-phone">{{ row.item.customerPhone }}</div>
            </div>
          </template>
          <!-- Store Cell -->
          <template v-slot:cell(storeName)="row">
            <div class="text-center">
              <span v-if="row.item.storeName">{{ row.item.storeName }}</span>
              <span v-else>-</span>
            </div>
          </template>

          <!-- Employee Cell -->
          <template v-slot:cell(employeeName)="row">
            <div class="employee-info">
              <div class="employee-name">
                {{ row.item.employeeName || '-' }}
              </div>
              <div v-if="row.item.employeeCode" class="employee-code">
                {{ row.item.employeeCode }}
              </div>
            </div>
          </template>

          <!-- Products Reasons Cell -->
          <template v-slot:cell(productReason)="row">
            <div class="product-reason-wrapper">
              <div
                v-for="(product, index) in row.item.products"
                :key="index"
                class="product-reason-row"
              >
                <div class="product-col">
                  {{ product.productName }}
                </div>
                <div class="reason-col">
                  {{ getReasonText(product.reasonId) }}
                </div>
              </div>
            </div>
          </template>

          <!-- Description Cell -->
          <template v-slot:cell(description)="row">
            <div class="description-cell">
              <span v-text="row.item.description" class="d-block"></span>
            </div>
          </template>

          <!-- Created At Cell -->
          <template v-slot:cell(createdAt)="row">
            <div>
              <span
                v-text="formatDate(row.item.createdAt, 'DD/MM/YYYY')"
                class="d-block"
              ></span>
            </div>
          </template>

          <!-- Actions Cell -->
          <template v-slot:cell(actions)="row">
            <div class="d-flex justify-content-center">
              <b-dropdown size="sm" id="dropdown-left" no-caret right>
                <template slot="button-content">
                  <i
                    style="font-size: 1rem; padding-right: 0px"
                    class="flaticon2-settings"
                  ></i>
                </template>
                <b-dropdown-item @click="editItem(row.item)">
                  <span style="color: #3f4254; font-size: 12px">
                    <i style="font-size: 1rem" class="flaticon2-pen"></i>
                    &nbsp; Chỉnh sửa
                  </span>
                </b-dropdown-item>
                <b-dropdown-item @click="confirmDelete(row.item)">
                  <span style="color: #3f4254; font-size: 12px">
                    <i
                      style="font-size: 1rem; color: #d33"
                      class="flaticon2-rubbish-bin-delete-button"
                    ></i>
                    &nbsp; Xóa
                  </span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-row>
          <b-col>
            <p class="mt-3 text-dark" style="font-weight: 500">
              Tổng số:
              {{ paginations.total }}
            </p>
          </b-col>
          <b-col>
            <BPaginationNavCustom
              :number-of-pages="paginations.totalPages || 1"
              :current-page="paginations.currentPage"
              :total-page="paginations.total"
              @page-change="onPageChange"
            />
          </b-col>
        </b-row>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import AutoSuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';

import { DP_CONFIG, formatDate, formatDBDate } from '@/utils/date';
import { createFile, getFileName } from '@/utils/file-download';

import { swalMixin } from '@/view/mixins';

import ApiService from '@/core/services/api.service';
import { UserService } from '@/core/api/user/user.service';
import UserModel from '@/core/models/User.model';

import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';

import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import { VclTable } from 'vue-content-loading';

import * as ENUM_NAME from '@/utils/enum-name';
import * as ENUM from '@/utils/enum';
import * as _ from 'lodash';
import * as moment from 'moment';

import debounce from 'debounce';
import { TIME_TRIGGER } from '@/utils/constants';
import { OrderCanNotServeModel } from '@/core/models/OrderCanNotServe.model';
import OrderCanNotServeService from '@/core/api/order-cant-serve/order-cant-serve.service';

export default {
  name: 'ListOrderCanNotServe',
  mixins: [swalMixin],
  components: {
    KTCodePreview,
    VclTable,
    BPaginationNavCustom,
    BDropdownCustom,
    AutoSuggest,
  },
  data() {
    return {
      orders: [],
      loading: false,
      perPage: 10,
      filters: {
        search: '',
        stores: [],
        reasons: [],
        employeeName: null,
        productName: '',
        fromDate: moment().subtract(15, 'days').format('DD/MM/YYYY'),
        toDate: moment().format('DD/MM/YYYY'),
      },

      employeeSuggestions: [],
      employeeSearchText: '',

      storeOptions: [],

      paginations: {
        total: 0,
        currentPage: 1,
        totalPages: 1,
      },

      configs: DP_CONFIG,

      fields: [
        {
          key: 'stt',
          label: 'STT',
          sortable: false,
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '60px',
          },
        },
        {
          key: 'customerInfo',
          label: 'Khách hàng',
          sortable: false,
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '150px',
          },
        },
        {
          key: 'storeName',
          label: 'Chi Nhánh',
          sortable: false,
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '150px',
          },
        },
        {
          key: 'employeeName',
          label: 'Nhân Viên',
          sortable: false,
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '150px',
          },
        },
        {
          key: 'productReason',
          label: 'Sản phẩm / Lý do',
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '400px',
          },
        },
        {
          key: 'description',
          label: 'Ghi chú',
          sortable: false,
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '200px',
          },
        },
        {
          key: 'createdAt',
          label: 'Ngày tạo',
          sortable: false,
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '120px',
          },
        },
      ],
      reasonOptions: this.getOptions(
        ENUM.ORDER_CANT_SERVE_REASON,
        ENUM_NAME.ORDER_CANT_SERVE_REASON_NAME,
      ),
    };
  },

  created() {
    this.fields.push({
      key: 'actions',
      label: '',
      sortable: false,
      thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
    });
  },

  mounted() {
    this.setBreadCrumbs();
    this.fetchStores();
    this.fetchData();
  },

  methods: {
    makeToastSuccess,
    makeToastFaile,
    formatDate,

    setBreadCrumbs() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Báo cáo', route: '/reports' },
        { title: 'Phiếu không phục vụ được', active: true },
      ]);
    },

    getOptions(enumValues, enumNames) {
      if (!enumValues || !enumNames) return [];

      return Object.entries(enumNames).map(([key, value]) => ({
        value: Number(key),
        text: value,
      }));
    },

    async fetchStores() {
      try {
        ApiService.setHeader();
        const response = await ApiService.get('/stores?getFull=true');
        const stores = response.data.data || [];

        this.storeOptions = stores.map((store) => ({
          value: String(store.id),
          text: store.name,
        }));
      } catch (error) {
        console.error('Error loading stores:', error);
        makeToastFaile('Không thể tải danh sách cửa hàng');
      }
    },

    selectEmployee(option) {
      if (option?.item) {
        this.filters.employeeName = option.item.label;
        this.employeeSearchText = option.item.label;
      } else {
        this.filters.employeeName = null;
        this.employeeSearchText = '';
      }
    },

    searchEmployees(searchQuery) {
      this.debounceSearch(searchQuery);
    },

    debounceSearch: debounce(function (searchQuery) {
      this.performSearch(searchQuery);
    }, TIME_TRIGGER),

    async performSearch(searchQuery) {
      this.employeeSuggestions = [];

      if (!searchQuery || searchQuery.length < 2) {
        return;
      }

      try {
        const { data } = await UserService.smartSearch({
          keyword: searchQuery,
          limit: 5,
        });

        const employees = UserModel.fromJsonArray(data?.data.data) || [];
        this.employeeSuggestions = employees.map((employee) => ({
          id: employee.id,
          label: employee.fullName,
          name: employee.fullName,
          code: employee.code,
        }));
      } catch (error) {
        console.error('Error loading employees:', error);
      }
    },

    async fetchData(page = 1) {
      this.loading = true;
      try {
        const storeIds = this.filters.stores.map((store) =>
          String(store.value),
        );

        const reasonIds = Array.isArray(this.filters.reasons)
          ? this.filters.reasons.map((r) => Number(r.value))
          : [];

        const createdFromDate = this.filters.fromDate
          ? formatDBDate(this.filters.fromDate)
          : null;
        const createdToDate = this.filters.toDate
          ? formatDBDate(this.filters.toDate)
          : null;

        const params = {
          ...this.filters,
          storeIds: storeIds,
          reasonIds: reasonIds,
          createdFromDate,
          createdToDate,
          page: page,
          limit: this.perPage,
        };

        const { data } = await OrderCanNotServeService.list(
          _.omitBy(params, (value) => !value),
        );

        const ordersData = OrderCanNotServeModel.fromJsonArray(
          data.data.data || [],
        );

        this.orders = ordersData.map((item, index) => ({
          ...item,
          stt: index + 1 + (page - 1) * this.perPage,
          employeeCode: item.employee?.code || null,
        }));

        this.paginations = data.data.paginations || {
          total: data.data.total_row || 0,
          currentPage: page,
          totalPages: data.data.total_page || 1,
        };
      } catch (error) {
        console.error('Error fetching orders:', error);
        makeToastFaile('Có lỗi xảy ra khi tải dữ liệu');
      } finally {
        this.loading = false;
      }
    },

    onFilter() {
      this.fetchData();
    },

    resetFilter() {
      this.filters = {
        customerPhoneName: '',
        stores: [],
        reasons: [],
        employeeName: null,
        productName: '',
        fromDate: moment().subtract(15, 'days').format('DD/MM/YYYY'),
        toDate: moment().format('DD/MM/YYYY'),
      };
      this.employeeSearchText = '';
      this.resetPagination();
      this.fetchData();
    },

    resetPagination() {
      this.paginations = {
        total: 0,
        currentPage: 1,
        totalPages: 1,
      };
    },

    onPageChange(page) {
      this.fetchData(page);
    },

    getReasonText(reasonId) {
      return (
        ENUM_NAME.ORDER_CANT_SERVE_REASON_NAME[reasonId] || 'Không xác định'
      );
    },

    createNew() {
      this.$router.push({ name: 'create-order-cant-serve' });
    },

    editItem(item) {
      this.$router.push({
        name: 'edit-order-cant-serve',
        params: { id: item.id },
      });
    },

    async confirmDelete(item) {
      const result = await this.popupSwalWarning({
        title: 'Xác nhận xóa',
        text: `Bạn có chắc chắn muốn xóa phiếu của khách hàng "${item.customerName}"?`,
      });

      if (result.isConfirmed) {
        await this.deleteItem(item.id);
      }
    },

    async deleteItem(id) {
      try {
        await OrderCanNotServeService.delete(id);
        makeToastSuccess('Xóa phiếu thành công');
        this.fetchData(this.paginations.currentPage);
      } catch (error) {
        console.error('Error deleting order:', error);
        makeToastFaile('Có lỗi xảy ra khi xóa phiếu');
      }
    },

    // Xuất Excel theo filter hiện tại
    async exportExcel() {
      try {
        const storeIds = this.filters.stores.map((store) =>
          String(store.value),
        );
        const reasonIds = Array.isArray(this.filters.reasons)
          ? this.filters.reasons.map((r) => Number(r.value))
          : [];

        const createdFromDate = this.filters.fromDate
          ? formatDBDate(this.filters.fromDate)
          : null;
        const createdToDate = this.filters.toDate
          ? formatDBDate(this.filters.toDate)
          : null;

        const params = {
          ...this.filters,
          storeIds: storeIds,
          reasonIds: reasonIds,
          createdFromDate,
          createdToDate,
        };

        const response = await OrderCanNotServeService.exportExcel(
          _.omitBy(params, (value) => !value),
        );
        const fileName =
          getFileName(response) || 'phieu-khong-phuc-vu-duoc.xlsx';
        createFile(response.data, fileName);
        makeToastSuccess('Xuất Excel thành công');
      } catch (error) {
        makeToastFaile('Có lỗi xảy ra khi xuất Excel');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.list-order-can-not-serve {
  .btn.btn-icon.btn-sm {
    height: calc(1.35em + 1.1rem + 2px);
    width: auto;
  }

  .dropdown-header {
    color: #3699ff !important;
  }
  .table th,
  .table td {
    padding: 0.75rem;
    vertical-align: middle;
    border-top: 1px solid #ecf0f3;
  }
  .table.b-table.table-sm
    > thead
    > tr
    > [aria-sort]:not(.b-table-sort-icon-left),
  .table.b-table.table-sm
    > tfoot
    > tr
    > [aria-sort]:not(.b-table-sort-icon-left) {
    background-position: right calc(1.5rem / 2) center;
    padding-right: calc(0.3rem + 0.65em);
  }

  .page-item.disabled {
    cursor: not-allowed;
    &.bv-d-xs-down-none .page-link {
      margin: 0.35rem 0.25rem;
      text-align: center;
    }
  }

  .customer-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: center;

    .customer-name {
      font-weight: 550;
      color: #3f4254;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }

    .customer-phone {
      color: #7e8299;
      font-size: 0.9rem;
    }
  }

  .employee-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: center;

    .employee-name {
      font-weight: 500;
      color: #3f4254;
    }

    .employee-code {
      color: #7e8299;
      font-size: 0.85rem;
    }
  }

  .description-cell {
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.5;
    word-break: break-word;
    white-space: pre-wrap;
  }

  .form-group label {
    font-weight: 500;
  }

  .white-space {
    white-space: pre-wrap;
  }

  .product-reason-wrapper {
    display: flex;
    flex-direction: column;
    margin: -0.75rem;
    height: calc(100% + 1.5rem);
  }

  .product-reason-row {
    display: flex;
    min-height: 48px;
    flex: 1;
    border-bottom: 1px solid #e4e6ef;
    transition: background-color 0.15s ease;

    &:last-child {
      border-bottom: none;
    }
    &:only-child {
      .product-col {
        border-right: 1px solid #e4e6ef;
      }
    }
    &:hover {
      background-color: #f5f8fa;
    }
  }

  .product-col,
  .reason-col {
    width: 50%;
    padding: 12px;
    text-align: left;
    display: flex;
    align-items: center;
    line-height: 1.5;
    word-break: break-word;
  }

  .product-col {
    border-right: 1px solid #e9ecef;
    align-self: stretch;
  }
}
</style>
